import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { SayfaGiris } from './sayfa-giris.js';
import { SayfaPanel } from './sayfa-panel.js';
import { SayfaNoktalar } from './noktalar/sayfa-noktalar.js';
import { SayfaNoktaDetay } from './noktalar/sayfa-nokta-detay.js';
import { SayfaDolapDetay } from './dolaplar/sayfa-dolap-detay.js';
import { SayfaKiralamalar } from './kiralamalar/sayfa-kiralamalar.js';
import { SayfaRezervasyonlar } from './rezervasyonlar/sayfa-rezervasyonlar.js';
import { SayfaOdemeler } from './odemeler/sayfa-odemeler.js';
import { SayfaIadeler } from './iadeler/sayfa-iadeler.js';
import { SayfaOlaylar } from './cihazlar/sayfa-olaylar.js';
import { SayfaMusteriler } from './musteriler/sayfa-musteriler.js';
import { SayfaFiyatKurallari } from './fiyat-kurallari/sayfa-fiyat-kurallari.js';
import { SayfaKuponlar } from './kuponlar/sayfa-kuponlar.js';
import { SayfaDoviz } from './doviz/sayfa-doviz.js';
import { SayfaKullanicilar } from './kullanicilar/sayfa-kullanicilar.js';
import { SayfaGelir } from './raporlar/sayfa-gelir.js';
import { SayfaAnalitik } from './raporlar/sayfa-analitik.js';
import { SayfaKarlilik } from './raporlar/sayfa-karlilik.js';
import { SayfaMasraflar } from './muhasebe/sayfa-masraflar.js';
import { SayfaOrtaklar } from './ortaklar/sayfa-ortaklar.js';
import { SayfaHakedis } from './ortaklar/sayfa-hakedis.js';
import { SayfaDestek } from './destek/sayfa-destek.js';
import { SayfaAyarlar } from './ayarlar/sayfa-ayarlar.js';
import { SayfaSmtp } from './ayarlar/sayfa-smtp.js';
import { SayfaSablonlar } from './ayarlar/sayfa-sablonlar.js';
import { SayfaNetgsm } from './ayarlar/sayfa-netgsm.js';
import { SayfaAkbank } from './ayarlar/sayfa-akbank.js';
import { SayfaPromosyon } from './bildirim/sayfa-promosyon.js';
import { SayfaDenetim } from './denetim/sayfa-denetim.js';
import { SayfaAktivite } from './aktivite/sayfa-aktivite.js';
import { SayfaYakinda } from './sayfa-yakinda.js';
import { AnaYerlesim } from './yerlesim.js';
import { useOturumStore } from './oturum.js';

const kok = createRootRoute({
  component: Outlet,
});

const giris = createRoute({
  getParentRoute: () => kok,
  path: '/giris',
  component: SayfaGiris,
});

const panelYerlesimi = createRoute({
  getParentRoute: () => kok,
  id: 'panel-yerlesimi',
  component: () => (
    <AnaYerlesim>
      <Outlet />
    </AnaYerlesim>
  ),
  beforeLoad: () => {
    const mevcut = useOturumStore.getState();
    if (!mevcut.yukleniyor && !mevcut.kullanici) {
      throw redirect({ to: '/giris' });
    }
  },
});

const panelDashboard = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel',
  component: SayfaPanel,
});

const panelNoktalar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/noktalar',
  component: SayfaNoktalar,
});

const panelNoktaDetay = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/noktalar/$id',
  component: SayfaNoktaDetay,
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: 'genel' | 'raspi' | 'dolaplar' | 'olaylar' | 'muhasebe'; dolap?: string } => {
    const TABLAR = ['genel', 'raspi', 'dolaplar', 'olaylar', 'muhasebe'] as const;
    return {
      tab:
        typeof search.tab === 'string' && (TABLAR as readonly string[]).includes(search.tab)
          ? (search.tab as 'genel' | 'raspi' | 'dolaplar' | 'olaylar' | 'muhasebe')
          : undefined,
      dolap: typeof search.dolap === 'string' ? search.dolap : undefined,
    };
  },
});

const panelDolapDetay = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/dolaplar/$id',
  component: SayfaDolapDetay,
});

const panelOlaylar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/olaylar',
  component: SayfaOlaylar,
});

const panelKiralamalar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/kiralamalar',
  component: SayfaKiralamalar,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    sekme?: 'aktif' | 'gecmis' | 'tumu';
    kira?: string;
    arama?: string;
    durum?:
      | 'OLUSTURULDU'
      | 'ODEME_BEKLIYOR'
      | 'AKTIF'
      | 'DOLAP_ACILDI'
      | 'KULLANILIYOR'
      | 'TESLIM_ALINIYOR'
      | 'TAMAMLANDI'
      | 'IPTAL_EDILDI'
      | 'SURESI_GECTI'
      | 'SORUNLU';
    noktaId?: string;
  } => {
    const sekmeler = ['aktif', 'gecmis', 'tumu'] as const;
    const durumlar = [
      'OLUSTURULDU',
      'ODEME_BEKLIYOR',
      'AKTIF',
      'DOLAP_ACILDI',
      'KULLANILIYOR',
      'TESLIM_ALINIYOR',
      'TAMAMLANDI',
      'IPTAL_EDILDI',
      'SURESI_GECTI',
      'SORUNLU',
    ] as const;
    return {
      sekme:
        typeof search.sekme === 'string' && (sekmeler as readonly string[]).includes(search.sekme)
          ? (search.sekme as 'aktif' | 'gecmis' | 'tumu')
          : undefined,
      kira: typeof search.kira === 'string' ? search.kira : undefined,
      arama: typeof search.arama === 'string' ? search.arama : undefined,
      durum:
        typeof search.durum === 'string' && (durumlar as readonly string[]).includes(search.durum)
          ? (search.durum as 'AKTIF')
          : undefined,
      noktaId: typeof search.noktaId === 'string' ? search.noktaId : undefined,
    };
  },
});

const panelRezervasyonlar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/rezervasyonlar',
  component: SayfaRezervasyonlar,
});

const panelOdemeler = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/odemeler',
  component: SayfaOdemeler,
});

const panelIadeler = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/iadeler',
  component: SayfaIadeler,
});

const panelMusteriler = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/musteriler',
  component: SayfaMusteriler,
});

const panelFiyatKurallari = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/fiyat-kurallari',
  component: SayfaFiyatKurallari,
});

const panelKuponlar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/kuponlar',
  component: SayfaKuponlar,
});

const panelDoviz = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/doviz',
  component: SayfaDoviz,
});

const panelKullanicilar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/kullanicilar',
  component: SayfaKullanicilar,
});

const panelRaporGelir = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/raporlar/gelir',
  component: SayfaGelir,
});

const panelRaporAnalitik = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/raporlar/analitik',
  component: SayfaAnalitik,
});

const panelRaporKarlilik = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/raporlar/karlilik',
  component: SayfaKarlilik,
});

const panelMuhasebeMasraflar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/muhasebe/masraflar',
  component: SayfaMasraflar,
});

const panelMuhasebeKarlilik = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/muhasebe/karlilik',
  component: SayfaKarlilik,
});

const panelOrtaklar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/ortaklar',
  component: SayfaOrtaklar,
});

const panelHakedis = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/hakedis',
  component: SayfaHakedis,
});

const panelDestek = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/destek',
  component: SayfaDestek,
});

const panelAyarlar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/ayarlar',
  component: SayfaAyarlar,
});

const panelSmtp = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/ayarlar/smtp',
  component: SayfaSmtp,
});

const panelSablonlar = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/ayarlar/sablonlar',
  component: SayfaSablonlar,
});

const panelNetgsm = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/ayarlar/netgsm',
  component: SayfaNetgsm,
});

const panelAkbank = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/ayarlar/akbank',
  component: SayfaAkbank,
});

const panelPromosyon = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/bildirim/promosyon',
  component: SayfaPromosyon,
});

const panelDenetim = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/denetim',
  component: SayfaDenetim,
});

const panelAktivite = createRoute({
  getParentRoute: () => panelYerlesimi,
  path: '/panel/aktivite',
  component: SayfaAktivite,
});

// Sonraki adımlarda gerçekleşecek modüller — placeholder Yakında ekranı
const YAKINDA_MODULLER: Array<{ yol: string; baslik: string }> = [];

const yakindaRotalari = YAKINDA_MODULLER.map((m) =>
  createRoute({
    getParentRoute: () => panelYerlesimi,
    path: m.yol,
    component: () => <SayfaYakinda baslik={m.baslik} />,
  }),
);

const kok_ile_yonlendir = createRoute({
  getParentRoute: () => kok,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/panel' });
  },
  component: () => null,
});

const rotaAgaci = kok.addChildren([
  giris,
  kok_ile_yonlendir,
  panelYerlesimi.addChildren([
    panelDashboard,
    panelNoktalar,
    panelNoktaDetay,
    panelDolapDetay,
    panelKiralamalar,
    panelRezervasyonlar,
    panelOdemeler,
    panelIadeler,
    panelMusteriler,
    panelFiyatKurallari,
    panelKuponlar,
    panelDoviz,
    panelKullanicilar,
    panelRaporGelir,
    panelRaporAnalitik,
    panelRaporKarlilik,
    panelMuhasebeMasraflar,
    panelMuhasebeKarlilik,
    panelOrtaklar,
    panelHakedis,
    panelDestek,
    panelAyarlar,
    panelSmtp,
    panelSablonlar,
    panelNetgsm,
    panelAkbank,
    panelPromosyon,
    panelDenetim,
    panelAktivite,
    panelOlaylar,
    ...yakindaRotalari,
  ]),
]);

export const yonlendirici = createRouter({ routeTree: rotaAgaci });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof yonlendirici;
  }
}
